import {
  PRODUCTION_STAGE_KIND_LABELS,
  PRODUCTION_STAGE_KINDS,
  type ProductionStageKind,
  type SlaRule,
  STAGE_COLORS,
  TICKET_PRIORITY_LABELS,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { EquipmentNav } from '@/components/equipment-bits';
import { Button, ErrorBox, PageHeader, Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { equipmentMetaQuery } from '@/lib/equipment';

const errorText = (err: Error) => (err instanceof ApiError && err.fields._ ? err.fields._ : err.message);

export function EquipmentSettingsPage() {
  const can = useCan();
  const meta = useQuery(equipmentMetaQuery);
  if (!can('equipment.settings.manage'))
    return <ErrorBox error={new Error('Настройки доступны администратору')} />;
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  const k = meta.dataUpdatedAt;
  return (
    <div>
      <PageHeader
        title="Настройки оборудования"
        description="Этапы сборки, модели стендов, категории и сроки заявок"
      />
      <EquipmentNav />
      <div className="flex max-w-[920px] flex-col gap-6">
        <StagesEditor key={`s${k}`} />
        <NamedEditor
          key={`m${k}`}
          kind="models"
          title="Модели стендов"
          hint="Выбираются в карточке стенда. Выключенная модель остаётся у старых записей."
          addLabel="Модель"
        />
        <NamedEditor
          key={`c${k}`}
          kind="categories"
          title="Категории заявок"
          hint="Ремонт, гарантия, пусконаладка… Выключенная категория остаётся у старых заявок."
          addLabel="Категория"
        />
        <SlaEditor key={`l${k}`} />
      </div>
    </div>
  );
}

interface StageDraft {
  key: string;
  id?: string;
  name: string;
  color: string;
  kind: ProductionStageKind;
}

function StagesEditor() {
  const qc = useQueryClient();
  const meta = useQuery(equipmentMetaQuery);
  const [rows, setRows] = useState<StageDraft[]>(() =>
    (meta.data?.stages ?? []).map((s) => ({ ...s, key: s.id })),
  );
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/equipment/settings/stages', {
        method: 'PUT',
        body: { stages: rows.map(({ id, name, color, kind }) => ({ id, name, color, kind })) },
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
  const set = (key: string, patch: Partial<StageDraft>) => {
    setSaved(false);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const moveRow = (i: number, d: -1 | 1) =>
    setRows((rs) => {
      const next = [...rs];
      const [row] = next.splice(i, 1);
      if (row) next.splice(i + d, 0, row);
      return next;
    });
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Этапы сборки</h2>
        <p className="text-[13px] text-steel">
          Колонки канбана производства. Новый стенд встаёт на первый этап. С этапа «Готов к отгрузке» стенд
          отгружают клиенту, «Пауза» — приостановленные. Этап со стендами удалить нельзя.
        </p>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((r, i) => (
          <li key={r.key} className="flex flex-wrap items-center gap-2 px-5 py-2">
            <div className="flex">
              <button
                type="button"
                aria-label="Выше"
                disabled={i === 0}
                onClick={() => moveRow(i, -1)}
                className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
              >
                <ArrowUp className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Ниже"
                disabled={i === rows.length - 1}
                onClick={() => moveRow(i, 1)}
                className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
              >
                <ArrowDown className="size-4" />
              </button>
            </div>
            <input
              aria-label="Название этапа"
              className="input min-w-[160px] flex-1"
              value={r.name}
              onChange={(e) => set(r.key, { name: e.target.value })}
            />
            <select
              aria-label="Вид этапа"
              className="input w-[170px]"
              value={r.kind}
              onChange={(e) => set(r.key, { kind: e.target.value as ProductionStageKind })}
            >
              {PRODUCTION_STAGE_KINDS.map((s) => (
                <option key={s} value={s}>
                  {PRODUCTION_STAGE_KIND_LABELS[s]}
                </option>
              ))}
            </select>
            <fieldset className="flex gap-1">
              <legend className="sr-only">Цвет</legend>
              {STAGE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={r.color.toLowerCase() === c.toLowerCase()}
                  aria-label={c}
                  onClick={() => set(r.key, { color: c })}
                  className={clsx(
                    'size-5 rounded-full',
                    r.color.toLowerCase() === c.toLowerCase() && 'ring-2 ring-signal ring-offset-1',
                  )}
                  style={{ background: c }}
                />
              ))}
            </fieldset>
            <button
              type="button"
              aria-label="Удалить этап"
              onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
              className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken hover:text-danger"
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const firstReady = rows.findIndex((r) => r.kind !== 'work');
            const draft: StageDraft = {
              key: crypto.randomUUID(),
              name: 'Новый этап',
              color: STAGE_COLORS[1],
              kind: 'work',
            };
            setRows((rs) =>
              firstReady === -1
                ? [...rs, draft]
                : [...rs.slice(0, firstReady), draft, ...rs.slice(firstReady)],
            );
          }}
        >
          <Plus className="size-4" aria-hidden />
          Этап
        </Button>
        <span className="flex-1" />
        {save.error && <span className="text-[13px] text-danger">{errorText(save.error)}</span>}
        {saved && !save.error && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить этапы
        </Button>
      </div>
    </section>
  );
}

function NamedEditor({
  kind,
  title,
  hint,
  addLabel,
}: {
  kind: 'models' | 'categories';
  title: string;
  hint: string;
  addLabel: string;
}) {
  const qc = useQueryClient();
  const meta = useQuery(equipmentMetaQuery);
  const [rows, setRows] = useState(() =>
    (meta.data?.[kind] ?? []).map((d) => ({ ...d, key: d.id as string })),
  );
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api(`/equipment/settings/${kind}`, {
        method: 'PUT',
        body: {
          [kind]: rows
            .filter((r) => r.name.trim())
            .map(({ id, name, isActive }) => ({ id: id || undefined, name, isActive })),
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
  const set = (key: string, patch: Partial<{ name: string; isActive: boolean }>) => {
    setSaved(false);
    setRows((rs) => rs.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  };
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">{title}</h2>
        <p className="text-[13px] text-steel">{hint}</p>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-3 text-[13.5px] text-steel">Пока пусто — добавьте первую запись.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center gap-3 px-5 py-2">
              <input
                aria-label="Название"
                className="input flex-1"
                value={r.name}
                onChange={(e) => set(r.key, { name: e.target.value })}
              />
              <label className="inline-flex shrink-0 items-center gap-2 text-[13.5px]">
                <input
                  type="checkbox"
                  checked={r.isActive}
                  onChange={(e) => set(r.key, { isActive: e.target.checked })}
                  className="size-4 accent-[var(--color-graphite)]"
                />
                Действует
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSaved(false);
            setRows((rs) => [...rs, { key: crypto.randomUUID(), id: '', name: '', isActive: true, sort: 0 }]);
          }}
        >
          <Plus className="size-4" aria-hidden />
          {addLabel}
        </Button>
        <span className="flex-1" />
        {save.error && <span className="text-[13px] text-danger">{errorText(save.error)}</span>}
        {saved && !save.error && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      </div>
    </section>
  );
}

function SlaEditor() {
  const qc = useQueryClient();
  const meta = useQuery(equipmentMetaQuery);
  const [rows, setRows] = useState<
    { priority: SlaRule['priority']; reactionHours: string; resolveHours: string }[]
  >(() =>
    (meta.data?.sla ?? []).map((r) => ({
      priority: r.priority,
      reactionHours: String(r.reactionHours),
      resolveHours: String(r.resolveHours),
    })),
  );
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/equipment/settings/sla', {
        method: 'PUT',
        body: {
          rules: rows.map((r) => ({
            priority: r.priority,
            reactionHours: r.reactionHours.replace(',', '.'),
            resolveHours: r.resolveHours.replace(',', '.'),
          })),
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
  const set = (p: SlaRule['priority'], patch: Partial<{ reactionHours: string; resolveHours: string }>) => {
    setSaved(false);
    setRows((rs) => rs.map((r) => (r.priority === p ? { ...r, ...patch } : r)));
  };
  return (
    <section className="panel">
      <div className="border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Сроки по заявкам</h2>
        <p className="text-[13px] text-steel">
          Реакция — успеть взять заявку в работу, решение — закрыть. Часы календарные, считаются от создания
          заявки. Просрочку сервис увидит в списке и получит уведомление. Новые сроки — для новых заявок.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-left">
          <thead>
            <tr className="border-b border-line text-[13px] text-steel">
              <th scope="col" className="px-5 py-2 font-medium">
                Приоритет
              </th>
              <th scope="col" className="px-5 py-2 font-medium">
                Реакция, ч
              </th>
              <th scope="col" className="px-5 py-2 font-medium">
                Решение, ч
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => (
              <tr key={r.priority}>
                <td className="px-5 py-2">{TICKET_PRIORITY_LABELS[r.priority]}</td>
                <td className="px-5 py-2">
                  <input
                    aria-label={`Реакция, ${TICKET_PRIORITY_LABELS[r.priority]}`}
                    inputMode="decimal"
                    className="input w-[110px]"
                    value={r.reactionHours}
                    onChange={(e) => set(r.priority, { reactionHours: e.target.value })}
                  />
                </td>
                <td className="px-5 py-2">
                  <input
                    aria-label={`Решение, ${TICKET_PRIORITY_LABELS[r.priority]}`}
                    inputMode="decimal"
                    className="input w-[110px]"
                    value={r.resolveHours}
                    onChange={(e) => set(r.priority, { resolveHours: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
        <span className="flex-1" />
        {save.error && <span className="text-[13px] text-danger">{errorText(save.error)}</span>}
        {saved && !save.error && <span className="text-[13px] text-ok">Сохранено</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить сроки
        </Button>
      </div>
    </section>
  );
}
